import axios from "axios";
import type { TFunction } from "i18next";
import {
  getBackendErrorMessageFromUnknown,
  getBackendValidationErrorMessage,
} from "services/api/backendError";
import type { AddStaffFormData } from "./addStaffPanel.contract";
import {
  isIbanChecksumFailure,
  toPaymentMethodFallbackErrorTranslationKey,
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "../shared/staff.paymentMethod.logic";
import { toStaffBackendErrorTranslationKey } from "../shared/staffBackendError.logic";

interface AddStaffErrorParams {
  readonly error: unknown;
  readonly formData: AddStaffFormData;
  readonly translate: TFunction;
}

interface InvalidPaymentMethodErrorParams {
  readonly backendMessage: string | null;
  readonly formData: AddStaffFormData;
  readonly translate: TFunction;
}

function getIbanChecksumErrorMessage({
  error,
  formData,
  translate,
}: AddStaffErrorParams): string | null {
  const validationMessage = getBackendValidationErrorMessage({
    field: "Request.Employment.PaymentMethodValue",
    payload: axios.isAxiosError(error) ? error.response?.data : null,
  });
  const hasChecksumFailure =
    formData.paymentMethodType === "InternationalBankAccount" &&
    isIbanChecksumFailure(validationMessage);
  return hasChecksumFailure
    ? translate("Staff.PaymentMethodInternationalBankAccountChecksumInvalid")
    : null;
}

function getInvalidPaymentMethodErrorMessage({
  backendMessage,
  formData,
  translate,
}: InvalidPaymentMethodErrorParams): string | null {
  if (backendMessage !== "Invalid payment method value") return null;
  const validationResult = validatePaymentMethodValue(
    formData.paymentMethodType,
    formData.paymentMethodValue,
  );
  return validationResult.type === "failure"
    ? translate(toPaymentMethodValidationTranslationKey(validationResult.error))
    : translate(
        toPaymentMethodFallbackErrorTranslationKey(formData.paymentMethodType),
      );
}

/**
 * The message shown when saving the add staff form fails, for a new worker as
 * well as a new employment.
 */
export function resolveAddStaffErrorMessage({
  error,
  formData,
  translate,
}: AddStaffErrorParams): string {
  const ibanChecksumError = getIbanChecksumErrorMessage({
    error,
    formData,
    translate,
  });
  if (ibanChecksumError != null) return ibanChecksumError;

  const backendMessage = getBackendErrorMessageFromUnknown(error);
  const paymentMethodError = getInvalidPaymentMethodErrorMessage({
    backendMessage,
    formData,
    translate,
  });
  if (paymentMethodError != null) return paymentMethodError;

  const mappedKey = toStaffBackendErrorTranslationKey(backendMessage);
  if (mappedKey) return translate(mappedKey);

  return translate("Alerts.SomethingWentWrong");
}
